import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import RatingDetails from "../../../GoService/components/RatingDetails";
import AdvertisementCol from "../../../Ads/components/AdvertisementCol";
import { useServiceFeedbackInfinite } from "../../../GoService/api/queries/useServiceFeedback";
import { ServiceFeedbackCard, ServiceFeedbackCardLoader } from "../../../GoService/components/ServiceFeedbackCard";

import EmptyReviewsFigure from "../../assets/images/empty-reviews.png";
import { AllReviewsBackButtonIcon } from "../../assets/icons/all-reviews-icons";

const REVIEW_LIMIT = 6;

const AllReviews = () => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "allReviews",
  });

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const entityUserId = searchParams.get("userId");
  const rating = Number(searchParams.get("rating") || undefined);

  const handleBack = () => {
    navigate(-1);
  };

  const ID = entityUserId || id;

  const {
    data: reviews,
    isLoading: isUserReviewsLoading,
    isError: iseUserReviewsHasError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useServiceFeedbackInfinite({
    userId: Number(ID),
    limit: REVIEW_LIMIT,
    rating,
  });

  const meta = reviews?.meta;
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const isLoading = isUserReviewsLoading || isFetchingNextPage;
  const isEmpty = reviews?.items.length === 0;

  if (!isLoading && isEmpty) {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.container}>
          <Typography sx={styles.title}>
            <IconButton size="small" onClick={handleBack}>
              <AllReviewsBackButtonIcon />
            </IconButton>
            {t("allServicesReviews")}
          </Typography>
          <Box sx={styles.emptyReviews}>
            <Box component="img" src={EmptyReviewsFigure} alt="empty-reviews" sx={styles.emptyReviewsImage} />
            <Typography sx={styles.emptyReviewsText}>{t("noReviews")}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <AdvertisementCol />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Typography sx={styles.title}>
          <IconButton size="small" onClick={handleBack}>
            <AllReviewsBackButtonIcon />
          </IconButton>
          {t("allServicesReviews")}
        </Typography>
        <RatingDetails meta={meta} />
        <Box />
        <Typography sx={styles.title}>{t("peopleReviews")}</Typography>

        {reviews?.items.map((review, index) => (
          <>
            <ServiceFeedbackCard key={review.id} feedback={review} isFullHeight={false} isFullWidth={true} />
            {index !== reviews?.items.length - 1 && (
              <Divider
                sx={{
                  borderColor: "#EAECF0",
                  margin: "0 -16px",
                }}
              />
            )}
          </>
        ))}
        {isLoading && [...Array(3)].map((_, index) => <ServiceFeedbackCardLoader key={index} />)}
        <div ref={ref} />
      </Box>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <AdvertisementCol />
      </Box>
    </Box>
  );
};

export default AllReviews;

const styles = {
  root: {
    display: "flex",
    gap: "16px",
  },
  emptyReviews: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    paddingTop: "64px",
  },
  emptyReviewsImage: {
    width: "146.724px",
    height: "140px",
    objectFit: "contain",
  },
  emptyReviewsText: {
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
    fontFamily: "Montserrat",
  },
  container: {
    height: "fit-content",
    width: { xs: "100%", lg: "636px" },
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  divider: {
    borderColor: "#EAECF0",
    margin: "0 -20px",
  },
};
